import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandMark from "@/components/BrandMark";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
    } catch {
      /* swallow — response is intentionally identical */
    }
    setBusy(false);
    setSent(true);
  };

  const inputCls = "mt-2 rounded-none border-0 border-b border-charcoal/40 bg-transparent focus-visible:ring-0 focus-visible:border-charcoal px-0 h-11";

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_1.1fr] bg-bone">
      <div className="relative hidden lg:flex flex-col justify-between p-14 bg-charcoal text-bone">
        <BrandMark variant="light" size="lg" asLink={false} />
        <div className="max-w-md">
          <p className="text-[13px] uppercase tracking-[0.28em] text-bone/85 mb-6">Members · Password reset</p>
          <h1 className="font-serif text-6xl xl:text-7xl leading-[0.95] tracking-tight">
            Forgot?<br/>
            <span className="italic text-bone/85">Happens.</span>
          </h1>
          <div className="h-px bg-bone/20 my-8" />
          <p className="text-sm text-bone/85 leading-relaxed max-w-sm">
            Enter your email — we'll send you a fresh reset link.
          </p>
        </div>
        <p className="text-[12px] font-mono uppercase tracking-[0.25em] text-bone/85">
          Research use only · Not for human consumption
        </p>
      </div>

      <div className="flex items-center justify-center px-6 py-16 lg:p-20">
        <div className="w-full max-w-md animate-fade-up" data-testid="forgot-password-page">
          <p className="text-[13px] uppercase tracking-[0.28em] text-stone mb-6">Reset password</p>
          <h2 className="font-serif text-5xl tracking-tight mb-3">Reset your password.</h2>
          <p className="text-sm text-stone mb-12">
            Enter the email associated with your account and we'll send a reset link. Links expire in 1 hour.
          </p>

          {sent ? (
            <div className="border border-borderSubtle bg-white p-6" data-testid="forgot-password-sent">
              <p className="overline text-oxblood mb-2">Sent</p>
              <p className="text-sm">
                If <span className="font-mono">{email}</span> exists in our system, a reset link is on its way.
                Check your inbox (and spam folder).
              </p>
              <Link
                to="/enter"
                className="mt-6 inline-block text-[13px] uppercase tracking-[0.22em] text-oxblood hover:text-charcoal"
              >
                Back to sign in →
              </Link>
            </div>
          ) : (
            <>
              <form onSubmit={submit} className="space-y-6" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email" className="text-[13px] uppercase tracking-[0.22em] text-stone">Email</Label>
                  <Input
                    id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    data-testid="forgot-email-input" className={inputCls}
                  />
                </div>
                <button
                  type="submit" disabled={busy}
                  className="w-full py-4 bg-charcoal text-bone tracking-[0.24em] text-xs uppercase hover:bg-oxblood transition-colors disabled:opacity-50 rounded-full"
                  data-testid="forgot-submit-btn"
                >
                  {busy ? "Sending…" : "Send reset link"}
                </button>
              </form>
              <Link
                to="/enter"
                className="mt-8 inline-block text-[13px] text-stone hover:text-charcoal uppercase tracking-[0.22em]"
                data-testid="forgot-back-btn"
              >
                ← Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
